import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from "@angular/core";
import {BokkzIbDataService, IncomeTaxEntity, OmschrijvingEnBedrag,} from "@snelstart-ventures/fiscaal/domain-logic";
import {FormArray, FormControl, FormGroup} from '@angular/forms';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  shareReplay,
  Subscription,
  switchMap,
  take,
  tap
} from "rxjs";
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {CommonModule} from '@angular/common';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

type PGAHulpMiddelenFormGroup = FormGroup<{
    omschrijving: FormControl<string | null>;
    bedrag: FormControl<number | null>;
}>;

@Component({
    selector: 'snelstart-venture-fiscaal-feature-hulpmiddelen',
    templateUrl: './hulpmiddelen.component.html',
    styleUrls: ['./hulpmiddelen.component.scss'],
    imports: [SharedUiButtonModule, SharedUiDetailsModule, SharedUiFormControlsModule, CommonModule],
})
export class HulpmiddelenComponent implements OnInit, OnDestroy {
    @Input() public id!: string;
    private dataService = inject(BokkzIbDataService);
    private destroyRef = inject(DestroyRef);
    private changeSubscriptions = new Subscription();

    public formGroup = new FormGroup({
        hulpmiddelen: new FormArray<PGAHulpMiddelenFormGroup>([]),
    });

    readonly incomeTaxEntity$: Observable<IncomeTaxEntity> = this.dataService.incomeTaxEntity$.pipe(
        filter((data): data is IncomeTaxEntity => !!data?.sstRelationId),
        takeUntilDestroyed(this.destroyRef),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    public ngOnInit(): void {
        this.incomeTaxEntity$.pipe(
          take(1),
          takeUntilDestroyed(this.destroyRef)
        ).subscribe((data) => {
            this.updateForm(data.aangifte?.persoonsgebondenAftrek?.pgaSpecifiekeZorgkosten?.hulpmiddelen || []);
        });
        this.subscribeToChanges();
    }

    ngOnDestroy(): void {
        this.changeSubscriptions.unsubscribe();
    }

    private subscribeToChanges(): void {
        this.changeSubscriptions.unsubscribe();
        this.changeSubscriptions = new Subscription();

        const extractRelevant = () =>
            this.hulpmiddelen.getRawValue().map((w) => ({
                omschrijving: w.omschrijving,
                bedrag: w.bedrag,
            }));

        this.changeSubscriptions.add(
            this.hulpmiddelen.valueChanges
                .pipe(
                    debounceTime(500),
                    tap(() => this.saveForm()),
                    map(() => extractRelevant()),
                    distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
                    switchMap(() => this.dataService.calculateLatestIncomeTaxReturn()),
                    takeUntilDestroyed(this.destroyRef),
                )
                .subscribe(),
        );
    }

    get hulpmiddelen(): FormArray {
        return this.formGroup.controls.hulpmiddelen;
    }

    addRijHulpmiddelen(): void {
        this.hulpmiddelen.push(this.createFormGroup());
    }

    verwijderRijHulpmiddelen(index: number): void {
        this.hulpmiddelen.removeAt(index, { emitEvent: false });
        this.formGroup.updateValueAndValidity({ emitEvent: false });
        this.formGroup.markAsDirty({ emitEvent: false });
        this.saveForm();
    }

    createFormGroup(hulpmiddel?: OmschrijvingEnBedrag): FormGroup {
        return new FormGroup({
            omschrijving: new FormControl<string | null>(hulpmiddel?.omschrijving ?? null),
            bedrag: new FormControl<number | null>(hulpmiddel?.bedrag ?? null),
        });
    }

    private updateForm(data: OmschrijvingEnBedrag[]): void {
        const lijst = data || [];
        const formArray = this.hulpmiddelen;

        while (formArray.length > lijst.length) {
            formArray.removeAt(formArray.length - 1, { emitEvent: false });
        }

        for (let i = 0; i < lijst.length; i++) {
            if (i < formArray.length) {
                const control = formArray.at(i) as FormGroup;
                control.patchValue(lijst[i], { emitEvent: false });
            } else {
                const formGroup = this.createFormGroup(lijst[i]);
                formArray.push(formGroup, { emitEvent: false });
            }
        }
    }

    saveForm(): void {
        const hulpmiddelen = this.formGroup
            .getRawValue()
            .hulpmiddelen.filter((restant) => restant.omschrijving || restant.bedrag)
            .map(
                (verplichtingen) =>
                    ({
                        omschrijving: verplichtingen.omschrijving,
                        bedrag: verplichtingen.bedrag,
                    }) as OmschrijvingEnBedrag,
            );

        this.dataService.patchIncomeTaxEntity((currentEntity) => {
            if (!currentEntity.aangifte) {
                return currentEntity;
            }

            return {
                ...currentEntity,
                aangifte: {
                    ...currentEntity.aangifte,
                    persoonsgebondenAftrek: {
                        ...currentEntity.aangifte.persoonsgebondenAftrek,
                        pgaSpecifiekeZorgkosten: {
                            ...currentEntity.aangifte.persoonsgebondenAftrek?.pgaSpecifiekeZorgkosten,
                            hulpmiddelen: hulpmiddelen,
                        },
                    },
                },
            } as IncomeTaxEntity;
        });
    }
}
